import { useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { FormField, Input } from "@cp/ui";
import { AuthLayout } from "../components/AuthLayout";
import { IconEye, IconEyeOff } from "../components/icons";
import { useAccountAuth } from "../contexts/AccountAuthContext";

function parseAuthError(raw: unknown): string {
  const msg = String(raw);
  if (msg.includes("account not activated")) {
    return "Konto noch nicht freigeschaltet. Schließen Sie zuerst das Abo ab oder warten Sie auf die Freigabe.";
  }
  if (msg.includes("invalid credentials")) {
    return "E-Mail oder Passwort falsch.";
  }
  if (msg.includes("already registered")) {
    return "Diese E-Mail ist bereits registriert.";
  }
  return msg;
}

export function LoginPage() {
  const { loggedIn, login } = useAccountAuth();
  const [params] = useSearchParams();
  const next = params.get("next");
  const product = params.get("product");
  const afterAuth =
    next && next.startsWith("/")
      ? next
      : product === "customer" || product === "customer_plus" || product === "customer_premium" || product === "advisor"
        ? `/billing?product=${product}`
        : "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (loggedIn) return <Navigate to={afterAuth} replace />;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await login(email, password);
    } catch (e) {
      setError(parseAuthError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      title="Willkommen zurück"
      description="Mit Ihrem Capital-Pilot-Konto anmelden"
    >
      <form className="auth-form" onSubmit={(event) => { event.preventDefault(); void submit(); }}>
        <FormField label="E-Mail" htmlFor="login-email" required>
          <Input
            id="login-email"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </FormField>
        <FormField label="Konto-Passwort" htmlFor="login-password" required>
          <div className="auth-form__password">
            <Input
              id="login-password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              type="button"
              className="auth-form__password-toggle"
              onClick={() => setShowPassword((value) => !value)}
              aria-label={showPassword ? "Passwort verbergen" : "Passwort anzeigen"}
            >
              {showPassword ? <IconEyeOff /> : <IconEye />}
            </button>
          </div>
        </FormField>
        <div className="auth-form__row">
          <span>Zugang nur für freigeschaltete Konten</span>
          <Link to="/forgot-password">Passwort vergessen?</Link>
        </div>
        <button
          type="submit"
          className="btn-primary auth-form__submit"
          disabled={busy || !email.trim() || !password}
        >
          {busy ? "Anmeldung läuft …" : "Anmelden"}
        </button>
        {error ? <p className="account-message account-message--error" role="alert">{error}</p> : null}
      </form>
    </AuthLayout>
  );
}
